<template>
  <div class="d-flex gap-1">
    <button
      v-if="this.point && this.point.instruction"
      type="button"
      :class="[
        'btn btn-info btn-sm',
        showLabel ? '' : 'rounded-circle'
      ]"
      @click="openInstruction"
      @mousedown="startLongPress"
      @mouseup="cancelLongPress"
      @mouseleave="cancelLongPress"
      @touchstart="startLongPress"
      @touchend="cancelLongPress"
      title="Ver Instrução (segurar para editar)"
    >
      <Icon v-if="!showLabel" icon="mdi-clipboard-text" class="text-white fs-4" />
      <span v-else>{{ point.instruction.name }}</span>
    </button>


    <button
      v-else
      type="button"
      :class="[
        'btn btn-secondary btn-sm',
        showLabel ? '' : 'rounded-circle'
      ]"
      @click="openBindModal"
      title="Bind Problem"
    >
      <Icon v-if="!showLabel" icon="mdi:link-variant" class="text-white fs-4" />
      <span v-else>Vincular Instrução</span>
    </button>

      <button v-if="isMetroUser(user) && point?.instruction?.playbook && !point?.playbookExecutionDate" class="btn btn-danger"
      @click="goAhead()">
      Executar Playbook
    </button>
  </div>

  <ModalContent 
    :is-modal-open="isModalOpen" 
    @close="closeModal" 
    @send="updateInstruction"
    :min-height="'500px'" 
    :title="modalTitle"
    :is-loading="isUpdatingInstruction"
  >

  <fieldset>
      <SearchableSelect 
        :items="instructionsList" 
        :inline="true"
        :auto-expand="true"
        :placeholder="modalPlaceholder"
        v-model="selectedInstruction" 
      />
    </fieldset>
  </ModalContent>
</template>

<script>
import ModalContent from '@/components/ModalContent.vue';
import SearchableSelect from '@/components/SearchableSelect.vue';
import { executePlaybook, instructionService, PointService, problemService } from '@/services/backend/ApiService';
import { Icon } from '@iconify/vue';
import { toast } from 'vue3-toastify';
import { isMetroUser } from '../../../zabbix-shared/utils/user';
import OnlineUserService from '@/services/OnlineUserService';

export default {
  name: 'BindProblem',
  components: {
    ModalContent,
    SearchableSelect,
    Icon
  },
  props: {
    point: {
      type: Object,
      required: true
    },
    companyId: {
      type: Number,
      required: true
    },
    showLabel: {
      type: Boolean,
      default: false
    }
  },
  emits: ['problemBound'],
  data() {
    return {
      user: null,
      isModalOpen: false,
      instructionsList: [],
      isUpdatingInstruction: false,
      selectedInstruction: null,
      longPressTimer: null,
      isLongPressed: false,
      isEditMode: false
    };
  },
  computed: {
    modalTitle() {
      return this.isEditMode ? 'Editar Instrução' : 'Víncule instrução ao problema';
    },
    modalPlaceholder() {
      return this.isEditMode ? 'Selecione uma nova instrução' : 'Selecione uma instrução';
    }
  },
  async mounted() {
    this.user = await OnlineUserService.waitForUser();
  },
  methods: {
    isMetroUser,
    openInstruction() {
      if (this.isLongPressed) {
        this.isLongPressed = false;
        return;
      }
      
      if (this.point && this.point.instruction) {
        window.open(this.point.instruction.link, '_blank');
      }
    },
    startLongPress(event) {
      this.isLongPressed = false;
      this.longPressTimer = setTimeout(() => {
        this.isLongPressed = true;
        this.openEditModal();
      }, 500);
    },
    cancelLongPress() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },
    openEditModal() {
      this.isEditMode = true;
      this.isModalOpen = true;
      this.loadInstructions();
      if (this.point && this.point.instruction) {
        this.selectedInstruction = this.point.instruction;
      }
    },
    openBindModal() {
      this.isEditMode = false;
      this.isModalOpen = true;
      this.loadInstructions();
    },
    closeModal() {
      this.isModalOpen = false;
      this.selectedInstruction = null;
      this.isEditMode = false;
    },
    async loadInstructions() {
      try {
        const instructions = await instructionService.findAll({ company: { id: this.companyId } });
        this.instructionsList = instructions.map(instruction => ({
          value: instruction,
          option: instruction.name
        }));
      } catch (error) {
        console.error('Erro ao carregar instruções:', error);
        toast.error('Erro ao carregar instruções');
      }
    },
    async goAhead() {
      await executePlaybook(this.point.instruction.playbook.id, {
        point: this.point,
      });

      toast.success('Playbook executado com sucesso!');
      // let pointData = await pointService.findOne(point.pointId);
      // pointData.playbookExecutionDate = new Date();
      // point.sharedPlaybookExecutionDate = formateDate(pointData.sharedPlaybookExecutionDate);
      
      // await pointService.update(pointData.id, pointData)

      // if (point.sectionJumpPlaybookId) {
      //   point.hasSharedPlaybook = true;
      //   point.existingSharedPlaybook = point.sectionJumpPlaybookId;
      // }
    },
    async executeSharedPlaybook(problem) {
      await executePlaybook(problem.existingSharedPlaybook);

      let problemData = await problemService.findOne(problem.problemId);
      problemData.sharedPlaybookExecutionDate = new Date();

      await problemService.update(problemData.id, problemData)
    },
    async updateInstruction() {
      if (!this.selectedInstruction) {
        toast.error('Selecione uma instrução');
        return;
      }

      this.isUpdatingInstruction = true;

      try {
        const existingPoints = await PointService.findAll({
          hostId: this.point.hostId,
          itemId: this.point.itemId,
          triggerId: this.point.triggerId,
          company: { id: this.companyId }
        });

        if (existingPoints && existingPoints.length > 0) {
          const pointToUpdate = existingPoints[0];
          await PointService.update(pointToUpdate.id, {
            instruction: this.selectedInstruction,
            company: { id: this.companyId }
          });
        } else {
          const newPoint = {
            hostId: this.point.hostId,
            itemId: this.point.itemId,
            triggerId: this.point.triggerId,
            host: this.point.hostName,
            item: this.point.itemName,
            trigger: this.point.triggerName,
            company: { id: this.companyId },
            instruction: this.selectedInstruction 
          };
          await PointService.create(newPoint);
        }

        this.$emit('problemBound');
        this.point.instruction = this.selectedInstruction;
        this.closeModal();
        toast.success(this.isEditMode ? 'Instrução atualizada com sucesso!' : 'Problema vinculado com sucesso!');
      } catch (error) {
        console.error('Erro ao atualizar instrução:', error);
        toast.error('Erro ao processar instrução');
      } finally {
        this.isUpdatingInstruction = false;
      }
    }
  }
}
</script>

<style scoped>
.btn.rounded-circle {
  height: 33px;
  width: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn:not(.rounded-circle) {
  padding: 0.375rem 0.75rem;
  min-height: 33px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

fieldset label {
  margin: 10px 0;
}
</style>

